import { toaster } from "@/_components/lib/ui/toaster";
import { usersService } from "@/_services/userServices/users";
import { useAuthStore } from "@/_stores/useAuthStore";
import {
  CheckoutOpenOptions,
  initializePaddle,
  Paddle,
  PaddleEventData,
} from "@paddle/paddle-js";
import { useCallback, useEffect, useState } from "react";
import {
  paddleService,
  paddleService as paymentService,
} from "@/_services/membershipServices/payment";

export const FRAME_TARGET = "checkout-container";

export const usePayment = (
  onEventCallback?: (event: PaddleEventData) => void,
) => {
  const { user } = useAuthStore();
  const isDevelopment = ["dev", "local"].includes(
    process.env.NEXT_PUBLIC_ENV || "",
  );
  const [paddle, setPaddle] = useState<Paddle | undefined>(undefined);

  useEffect(() => {
    const initPaddle = async () => {
      const paddle = await initializePaddle({
        token: process.env.NEXT_PUBLIC_PADDLE_CLIENT_KEY || "",
        environment: isDevelopment ? "sandbox" : "production",
        checkout: {
          settings: {
            displayMode: "inline",
            frameTarget: FRAME_TARGET,
            frameInitialHeight: 450,
            frameStyle:
              "width: 100%; min-width: 312px; background-color: transparent; border: none;",
          },
        },
        eventCallback: (event) => {
          if (event.name === "checkout.customer.created") {
            if (event.data && event.data.customer.id) {
              usersService.updateCustomerId(event.data.customer.id);
            }
          }
          onEventCallback?.(event);
        },
      });
      setPaddle(paddle);
    };
    initPaddle();
  }, [onEventCallback]);

  const openCheckout = useCallback(
    async (priceItem: { priceId: string; quantity: number }) => {
      if (!paddle) {
        toaster.error({
          title: "Error",
          description: "Paddle not initialized",
        });
        throw new Error("Paddle not initialized");
      }
      const checkoutOpts: CheckoutOpenOptions = {
        items: [
          {
            priceId: priceItem.priceId,
            quantity: priceItem.quantity,
          },
        ],
        customData: {
          email: user?.userInfo.email || "example@gm.co",
          userId: user?.userId || null,
        },
      };

      let customerAuthToken: string | undefined;
      let paymentMethodId: string | undefined;

      if (user?.paymentCustomerId) {
        try {
          const { token, payment_method_id } =
            await paymentService.getTokenAndSavedMethod();
          customerAuthToken = token;
          paymentMethodId = payment_method_id;
        } catch (error) {
          console.error("Failed to get token and saved method", error);
        }
      }

      if (customerAuthToken && paymentMethodId) {
        checkoutOpts.customerAuthToken = customerAuthToken;
        checkoutOpts.savedPaymentMethodId = paymentMethodId;
      } else if (user?.userInfo.email) {
        checkoutOpts.customer = {
          email: user.userInfo.email,
        };
      }

      paddle.Checkout.open(checkoutOpts);
    },
    [paddle, user?.paymentCustomerId, user?.userInfo.email],
  );

  const openUpdatePaymentDetail = async () => {
    if (!paddle) {
      toaster.error({
        title: "Error",
        description: "Paddle not initialized",
      });
      return;
    }
    const { id } = await paddleService.getUpdatePaymentDetailTnxId();
    paddle.Checkout.open({
      transactionId: id,
    });
  };
  const closeCheckout = useCallback(() => {
    paddle?.Checkout.close();
  }, [paddle]);

  return {
    openCheckout,
    closeCheckout,
    openUpdatePaymentDetail,
    isPaddleReady: !!paddle,
  };
};
